import {
    Component,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    IServerSideDatasource,
    IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { Store } from '@ngrx/store';
import { first, forkJoin, of } from 'rxjs';

import { RouterService } from '@services/router.service';
import { UserService } from '@services/api/user/user.service';
import { UserDataService } from '@services/data/user-data.service';
import { ColumnOptionsService } from '@services/column-options.service';
import { DocumentService } from '@services/api/document/document.service';
import { DiscrepancyService } from '@services/api/discrepancy/discrepancy.service';
import { WorkflowService } from '@services/api/workflow/workflow.service';
import { TextFilterComponent } from '@grid-table/filter/text-filter/text-filter.component';
import { PriorityCellComponent } from '@grid-table/cell/priority-cell/priority-cell.component';
import { TableComponent } from '@grid-table/table.component';
import { ClickableCellComponent } from '@grid-table/cell/clickable-cell/clickable-cell.compoent';
import { GrayBoxCellComponent } from '@grid-table/cell/gray-box-cell/gray-box-cell.component';
import { DocumentNameCellComponent } from '@grid-table/cell/document-name-cell/document-name-cell.component';
import { SelectFilterComponent } from '@grid-table/filter/select-filter/select-filter.component';
import { DateFilterComponent } from '@grid-table/filter/date-filter/date-filter.component';
import { RangeFilterComponent } from '@grid-table/filter/range-filter/range-filter.component';
import { Range } from '@models/shared/range';
import { DiscrepancyFilterOptions, BulkRequestItem } from '@models/discrepancy';
import {
    BulkTakeDiscrepancyRequest,
    BulkActionRequest,
    DiscrepancyViewQueryParameters,
} from '@models/discrepancy/bulk-request';
import { Workflow } from '@models/workflow/workflow';
import { CustomColumn } from '@models/workflow/custom-column';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import {
    DiscrepancyColumn,
    DocumentDetailTab,
    getAgeInWFValue,
    getDiscrepancyId,
} from '@main/utils';
import { saveSummaryPanelData } from 'app/store/actions/document.actions';
import DATA_QA from '@automation/data-qa.json';
import {
    isFilterApplied,
    getPageIndex,
    getFilterQuery,
    getSortQuery,
    hideHiddenColumns,
    setFilterAndSorting,
} from '@helpers/table';
import { customColumnsToColDefs } from '@helpers/custom-column';

@Component({
    selector: 'app-discrepancy-view',
    templateUrl: './discrepancy-view.component.html',
    imports: [TableComponent],
})
export class DiscrepencyViewComponent implements OnInit, OnChanges, OnDestroy {
    @Input() workflowAndRepo: Workflow | undefined;
    @Input() states: number[] = [];
    @Input() showMyItems: boolean = false;

    error = false;
    filterApplied: boolean = false;
    columnDefs: ColDef[] = [];
    gridOptions: GridOptions = {
        onRowSelected: this.onRowSelected.bind(this),
        onPaginationChanged: this.onRowSelected.bind(this),
        onFilterChanged: () => this.syncGlobalSelectionToVisibleRows(),
        onModelUpdated: () => this.syncGlobalSelectionToVisibleRows(),
        onFirstDataRendered: () => this.syncGlobalSelectionToVisibleRows(),
        onRowDataUpdated: () => this.syncGlobalSelectionToVisibleRows(),
    };
    hiddenColumnOptions: string[] = [];
    selectedBulkData: BulkRequestItem[] = [];
    selectedAllAcrossPages: boolean = false;
    private lastFilterSignature: string | null = null;
    private lastStatesSignature: string | null = null;
    private deselectedMap: Map<number, Set<number>> = new Map();
    private selectedMap: Map<number, Set<number>> = new Map();
    // Tracks the workflow status (chip name) of each individually selected
    // discrepancy so that a status-chip removal can prune only the selections
    // that belong to the removed status.
    private selectedStatusMap: Map<number, string> = new Map();
    resultCount: number = 0;
    firstFetch = true;
    savedPageIndex: number | undefined;
    private _gridApi!: GridApi;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    readonly dataQa = DATA_QA;

    private readonly store = inject(Store);
    private readonly userService = inject(UserService);
    private readonly routerService = inject(RouterService);
    private readonly userDataService = inject(UserDataService);
    private readonly documentService = inject(DocumentService);
    private readonly discrepancyService = inject(DiscrepancyService);
    private readonly columnOptionsService = inject(ColumnOptionsService);
    private readonly workflowService = inject(WorkflowService);

    private customColumns: CustomColumn[] = [];
    private currentFilterOptions: DiscrepancyFilterOptions =
        DiscrepancyFilterOptions.initial();

    get filterModel() {
        return this._gridApi.getFilterModel();
    }

    get columnState() {
        return this._gridApi.getColumnState();
    }

    get hasSelectedRow() {
        return this.selectedAllAcrossPages
            ? this.resultCount > 0
            : this.selectedMap.size > 0;
    }

    get selectedDiscrepancyCount() {
        if (this.selectedAllAcrossPages) {
            const deselectedCount = Array.from(
                this.deselectedMap.values(),
            ).reduce((sum, set) => sum + set.size, 0);
            return this.resultCount - deselectedCount;
        }
        // Derive from selectedMap (not the visible-rows-only selectedBulkData)
        // so individually selected discrepancies stay counted across pages.
        return Array.from(this.selectedMap.values()).reduce(
            (sum, set) => sum + set.size,
            0,
        );
    }

    private buildBulkAction(): BulkActionRequest {
        if (this.selectedAllAcrossPages) {
            const documentAndDiscrepancies = Array.from(
                this.deselectedMap.entries(),
            ).map(([documentId, set]) => ({
                documentId,
                discrepancies: Array.from(set.values()),
            }));
            return {
                selectedAll: true,
                documentAndDiscrepancies,
            };
        }
        const documentAndDiscrepancies = Array.from(
            this.selectedMap.entries(),
        ).map(([documentId, set]) => ({
            documentId,
            discrepancies: Array.from(set.values()),
        }));
        return {
            selectedAll: false,
            documentAndDiscrepancies,
        };
    }

    buildBulkRequest(): BulkTakeDiscrepancyRequest {
        const action = this.buildBulkAction();
        const fm = this.filterModel;
        const q: DiscrepancyViewQueryParameters = {
            documentName: fm['documentName']?.filter ?? null,
            workflowId: this.workflowAndRepo?.workflowId ?? null,
            states: this.states ?? [],
            showOnlyUserItems: this.showMyItems,
            reasonId: this.parseNumberOrNull(fm['reason']?.filter),
            priority: this.parseNumberOrNull(fm['priority']?.filter),
            status: this.parseNumberOrNull(fm['status']?.filter),
            analystName: fm['analyst']?.filter ?? null,
            stationId: this.parseNumberOrNull(fm['station']?.filter),
            primaryFixerId: this.parseNumberOrNull(fm['primaryFixer']?.filter),
            createdDateRangeFilter: fm['createdAt']?.filter ?? null,
            ageRangeFilter: fm['age']?.filter ?? null,
            takenDateRangeFilter: fm['taken']?.filter ?? null,
        };
        return {
            action: action,
            discrepancyViewQueryParameters: q,
            documentViewQueryParameters: null,
        };
    }

    private parseNumberOrNull(value: any): number | null {
        if (value === undefined || value === null || value === '') return null;
        const n = Number(value);
        return isNaN(n) ? null : n;
    }

    ngOnInit(): void {
        this.columnDefs = [
            ...this.getColumnDefs(DiscrepancyFilterOptions.initial()),
        ];
        this.handleColumnOptions();
        this.loadColumnsAndFilters();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const workflowAndRepoChange = changes['workflowAndRepo'];
        if (workflowAndRepoChange && !workflowAndRepoChange.firstChange) {
            this.resetFilters();
            this.loadColumnsAndFilters(true);
        }
    }

    ngOnDestroy() {
        this._subscriptions['get-inbox-choices'] = this.userService
            .getInboxChoices()
            .subscribe({
                complete: () => {
                    this._subscriptions.unsubscribeAllSafe();
                },
            });
    }

    onGridReady(event: GridReadyEvent): void {
        this._gridApi = event.api;

        const datasource = this.getAllDiscrepancies();
        this._gridApi.setGridOption('serverSideDatasource', datasource);
        this.applySavedColumnVisibility();
    }

    setSelectedRows() {
        const result = new Map<number, number[]>();

        if (this._gridApi)
            this._gridApi.forEachNode((rowNode) => {
                if (rowNode.isSelected()) {
                    const { documentId, id } = rowNode.data;

                    if (!result.has(documentId)) {
                        result.set(documentId, []);
                    }
                    result.get(documentId)!.push(id);
                }
            });

        this.selectedBulkData = Array.from(result.entries()).map(
            ([documentId, discrepancies]) => ({ documentId, discrepancies }),
        );
    }

    resetFilters() {
        if (!this._gridApi.isDestroyed()) {
            this._gridApi.refreshHeader();
            this._gridApi.setFilterModel(null);
        }
    }

    refreshData(purge = true) {
        if (!this._gridApi.isDestroyed()) {
            this._gridApi.refreshServerSide({ purge });
        }
    }

    private setSavedFilter() {
        this._subscriptions['set-saved-filter'] =
            this.userDataService.userInboxChoices$.subscribe({
                next: (choices) => {
                    if (choices) {
                        this.savedPageIndex =
                            choices.discrepancy.pageIndex ?? 1;

                        const { pageIndex, ...filterAndSort } =
                            choices.discrepancy;

                        // Normalize custom column filter keys to lowercase
                        // to match ag-Grid colIds (which are always lowercase).
                        // For example, "ecnDate" from the response becomes "ecndate"
                        // so it matches the custom column's colId.
                        const customColumnNameSet = new Set(
                            this.customColumns.map((c) =>
                                c.columnName.toLowerCase(),
                            ),
                        );
                        const normalizedFilterAndSort = Object.fromEntries(
                            Object.entries(filterAndSort).map(
                                ([key, value]) => {
                                    const lowerKey = key.toLowerCase();
                                    const isCustomColumn =
                                        customColumnNameSet.has(lowerKey);
                                    return isCustomColumn
                                        ? [lowerKey, value]
                                        : [key, value];
                                },
                            ),
                        );

                        // Also normalize orderBy if it's a custom column
                        if (
                            normalizedFilterAndSort['orderBy'] &&
                            customColumnNameSet.has(
                                (
                                    normalizedFilterAndSort['orderBy'] as string
                                ).toLowerCase(),
                            )
                        ) {
                            normalizedFilterAndSort['orderBy'] = (
                                normalizedFilterAndSort['orderBy'] as string
                            ).toLowerCase();
                        }

                        setFilterAndSorting(
                            this._gridApi,
                            normalizedFilterAndSort,
                        );
                        this.columnOptionsService.syncColumnConfig(
                            choices.columnConfig,
                        );
                    }
                },
            });
    }

    private onRowSelected(event: any) {
        if (this.selectionSyncInProgress) return;

        if (event?.source === 'paginationChanged') return;

        // uiSelectAll toggles global selection mode
        if (event?.source === 'uiSelectAll') {
            this.handleUiSelectAllEvent(event);
        }

        if (this.selectedAllAcrossPages) {
            this.handleGlobalSelectionEvent(event);
            this.selectedBulkData = [];
            this.syncGlobalSelectionToVisibleRows(true);
        } else {
            this.handleNormalSelectionEvent(event);
            this.setSelectedRows();
        }
    }

    private handleUiSelectAllEvent(event: any) {
        if (event.node?.selected && !this.selectedAllAcrossPages) {
            this.selectedAllAcrossPages = true;
            this.deselectedMap.clear();
            this.selectedMap.clear();
            this.selectedStatusMap.clear();
        } else if (!event.node?.selected && this.selectedAllAcrossPages) {
            this.selectedAllAcrossPages = false;
            this.deselectedMap.clear();
            this.selectedMap.clear();
            this.selectedStatusMap.clear();
            if (this._gridApi) this._gridApi.deselectAll();
        }
    }

    private handleGlobalSelectionEvent(event?: any) {
        if (!event?.node?.data) return;
        const { documentId, id } = event.node.data;
        if (!event.node.selected) {
            if (!this.deselectedMap.has(documentId)) {
                this.deselectedMap.set(documentId, new Set());
            }
            this.deselectedMap.get(documentId)!.add(id);
        } else {
            const set = this.deselectedMap.get(documentId);
            if (set) {
                set.delete(id);
                if (set.size === 0) this.deselectedMap.delete(documentId);
            }
        }
        // Track diff
        if (!this.pendingDiscrepancyUpdates.has(documentId)) {
            this.pendingDiscrepancyUpdates.set(documentId, new Set());
        }
        this.pendingDiscrepancyUpdates.get(documentId)!.add(id);
    }

    private handleNormalSelectionEvent(event?: any) {
        if (!event?.node?.data) return;
        const { documentId, id, status } = event.node.data;
        if (event.node.selected) {
            if (!this.selectedMap.has(documentId)) {
                this.selectedMap.set(documentId, new Set());
            }
            this.selectedMap.get(documentId)!.add(id);
            this.selectedStatusMap.set(id, status);
        } else {
            const set = this.selectedMap.get(documentId);
            if (set) {
                set.delete(id);
                if (set.size === 0) this.selectedMap.delete(documentId);
            }
            this.selectedStatusMap.delete(id);
        }
    }

    private selectionSyncInProgress = false;
    // Diff optimization for discrepancy rows (simpler: only track changed discrepancy ids)
    private readonly pendingDiscrepancyUpdates: Map<number, Set<number>> =
        new Map();
    private pendingFullRescan: boolean = false;

    private syncGlobalSelectionToVisibleRows(immediate: boolean = false) {
        if (!this.selectedAllAcrossPages || !this._gridApi) return;
        const exec = () => {
            if (!this.selectedAllAcrossPages || !this._gridApi) return;
            if (this.selectionSyncInProgress) return;
            this.selectionSyncInProgress = true;
            try {
                if (this.pendingFullRescan) {
                    this.runFullScan();
                    this.pendingFullRescan = false;
                    this.clearDiffSets();
                    return;
                }
                this.pendingDiscrepancyUpdates.forEach((discSet, docId) => {
                    discSet.forEach((discId) => {
                        const rowNode = this._gridApi.getRowNode(
                            discId.toString(),
                        );
                        if (!rowNode?.data) return;
                        const entry = this.deselectedMap.get(docId);
                        const shouldSelect = !entry?.has(discId);
                        if (rowNode.isSelected() !== shouldSelect)
                            rowNode.setSelected(shouldSelect);
                    });
                });
                // Header & remaining nodes if needed
                this._gridApi.refreshHeader();
                this.clearDiffSets();
            } finally {
                this.selectionSyncInProgress = false;
            }
        };
        if (immediate) exec();
        else requestAnimationFrame(exec);
    }

    private runFullScan() {
        let changed = false;
        this._gridApi.forEachNode((node: IRowNode) => {
            if (!node.data) return;
            const { documentId, id } = node.data;
            const isDeselected =
                this.deselectedMap.get(documentId)?.has(id) ?? false;
            const shouldSelect = !isDeselected;
            if (node.isSelected() !== shouldSelect) {
                node.setSelected(shouldSelect);
                changed = true;
            }
        });
        if (this.deselectedMap.size === 0) {
            if (!changed) this._gridApi.selectAll();
            this._gridApi.forEachNode((n: IRowNode) => {
                if (!n.data) return;
                if (!n.isSelected()) n.setSelected(true);
            });
        }
        this._gridApi.refreshHeader();
    }

    private clearDiffSets() {
        this.pendingDiscrepancyUpdates.clear();
    }

    // Load custom columns AND filter options together so the final columnDefs
    // (static + custom) are pushed to ag-Grid BEFORE setSavedFilter runs.
    // This way custom columns participate in the same uniform restore path as
    // static columns (e.g. "taken"): setFilterAndSorting → ag-Grid sees the
    // colId and restores filter/sort without any per-column special-casing.
    private loadColumnsAndFilters(ignoreUserChoices = false) {
        const workflowId = this.workflowAndRepo?.workflowId;
        const customColumns$ = workflowId
            ? this.workflowService.getCustomColumns(workflowId)
            : of<CustomColumn[]>([]);
        const filterOptions$ =
            this.discrepancyService.getDiscrepanciesFilterOptions(
                this.workflowAndRepo,
            );

        this._subscriptions['load-columns-and-filters'] = forkJoin({
            customColumns: customColumns$,
            filterOptions: filterOptions$,
        }).subscribe({
            next: ({ customColumns, filterOptions }) => {
                this.customColumns = customColumns;
                this.currentFilterOptions = filterOptions;

                this.columnDefs = [...this.getColumnDefs(filterOptions)];
                // Push columnDefs directly to ag-Grid so all columns (static
                // + custom) are registered before setSavedFilter applies
                // filter/sort state; the Angular `[columnDefs]` binding
                // does not propagate synchronously.
                if (this._gridApi && !this._gridApi.isDestroyed()) {
                    this._gridApi.setGridOption('columnDefs', this.columnDefs);
                }
                setTimeout(() => this.applySavedColumnVisibility());
            },
            complete: () => {
                if (!ignoreUserChoices) this.setSavedFilter();
            },
        });
    }

    private handleColumnOptions() {
        this._subscriptions['column-options'] =
            this.columnOptionsService.discColumnOptions$.subscribe({
                next: (options) => {
                    this.hiddenColumnOptions = options.hiddenColumns;

                    const option = options.lastToggled;
                    if (
                        option &&
                        this._gridApi &&
                        !this._gridApi.isDestroyed()
                    ) {
                        // Single user toggle: apply imperatively (no flicker).
                        this._gridApi.setColumnsVisible(
                            [option.id],
                            option.checked,
                        );
                    } else if (this._gridApi && !this._gridApi.isDestroyed()) {
                        // Restore path (no lastToggled, e.g. saved config sync):
                        // rebuild columnDefs so custom columns' declarative `hide`
                        // matches the saved hidden state. ag-Grid re-applies colDef
                        // `hide` on every columnDefs update, which would otherwise
                        // re-show hidden custom columns (e.g. ecnDate) after refresh.
                        this.columnDefs = [
                            ...this.getColumnDefs(this.currentFilterOptions),
                        ];
                        this._gridApi.setGridOption(
                            'columnDefs',
                            this.columnDefs,
                        );
                    }

                    // Defer visibility restore to allow ag-Grid to process any pending
                    // columnDefs changes before we apply column visibility state.
                    setTimeout(() => this.applySavedColumnVisibility());
                },
            });
    }

    private applySavedColumnVisibility() {
        hideHiddenColumns(this._gridApi, this.hiddenColumnOptions);
    }

    private getAllDiscrepancies(): IServerSideDatasource {
        return {
            getRows: (params) => {
                params.api.hideOverlay();
                this.filterApplied = isFilterApplied(params.request);
                const currentSignature = JSON.stringify(
                    params.request.filterModel || {},
                );
                const filterChanged =
                    this.lastFilterSignature !== currentSignature;
                const statesSignature = JSON.stringify(this.states ?? []);
                const statesChanged =
                    this.lastStatesSignature !== statesSignature;
                if (filterChanged || statesChanged) {
                    this.lastFilterSignature = currentSignature;
                    this.lastStatesSignature = statesSignature;
                    if (this.selectedAllAcrossPages) {
                        // Select-all mode: keep "all selected" semantics across
                        // filter/chip changes by dropping explicit deselections
                        // and re-selecting the new result set.
                        this.deselectedMap.clear();
                        this.pendingFullRescan = true;
                    } else if (filterChanged && this.filterApplied) {
                        // A column filter changed: reset the normal selection
                        // (existing behavior).
                        this.selectedMap.clear();
                        this.selectedStatusMap.clear();
                        this.selectedBulkData = [];
                        this._gridApi.deselectAll();
                    }
                    // Status-chip (states) changes in normal mode are handled by
                    // the parent via pruneSelectionByStatuses() before the
                    // refresh (only the removed status is dropped), and the
                    // remaining selection is re-applied once the new data loads
                    // below; so we intentionally do not wipe here.
                }

                this.discrepancyService
                    .getAllDiscrepancies(this.getQueryParams(params.request))
                    .pipe(first())
                    .subscribe({
                        next: (data) => {
                            params.api.hideOverlay();

                            if (data) {
                                params.success({
                                    rowData: data.items,
                                    rowCount: data.totalItems ?? -1,
                                });
                                if (data.items.length === 0) {
                                    params.api.showNoRowsOverlay();
                                }
                                this.resultCount = data.totalItems ?? 0;
                                this.goToSavedPage();
                                if (this.selectedAllAcrossPages) {
                                    setTimeout(() => {
                                        this._gridApi.forEachNode((rowNode) => {
                                            if (!rowNode.data) return;
                                            const { documentId, id } =
                                                rowNode.data;
                                            const deselected =
                                                this.deselectedMap.get(
                                                    documentId,
                                                );
                                            const isDeselected =
                                                deselected?.has(id) ?? false;
                                            rowNode.setSelected(!isDeselected);
                                        });
                                    });
                                } else {
                                    // Re-apply the retained normal-mode
                                    // selection to the freshly loaded rows so a
                                    // status-chip change (or any refresh) keeps
                                    // the still-valid selections checked.
                                    setTimeout(() =>
                                        this.reapplyNormalSelectionToVisibleRows(),
                                    );
                                }
                            } else {
                                params.fail();
                            }
                        },
                        error: () => {
                            params.api.hideOverlay();
                            this.error = true;
                        },
                    });
            },
        };
    }

    private reapplyNormalSelectionToVisibleRows() {
        if (
            !this._gridApi ||
            this._gridApi.isDestroyed() ||
            this.selectedAllAcrossPages
        ) {
            return;
        }
        this.selectionSyncInProgress = true;
        let selectionChanged = false;
        try {
            this._gridApi.forEachNode((node: IRowNode) => {
                if (!node.data) return;
                const shouldSelect =
                    this.selectedMap
                        .get(node.data.documentId)
                        ?.has(node.data.id) ?? false;
                if (node.isSelected() !== shouldSelect) {
                    node.setSelected(shouldSelect);
                    selectionChanged = true;
                }
            });
        } finally {
            this.selectionSyncInProgress = false;
        }
        // Recompute the header "select all" checkbox so it does not keep a
        // stale indeterminate state after the selection has been cleared.
        // Only do this when a row's selection actually changed: refreshHeader()
        // recreates every header cell including floating filters, which steals
        // focus from an in-progress filter input on every unrelated data reload
        // otherwise (e.g. simply typing into a column filter).
        if (selectionChanged) {
            this._gridApi.refreshHeader();
        }
        this.setSelectedRows();
    }

    /**
     * Drops individually selected discrepancies whose workflow status matches
     * one of the removed status chips, keeping every other selection intact.
     * Called by the parent when a status chip is de-selected.
     */
    pruneSelectionByStatuses(statuses: string[]) {
        if (!statuses.length || this.selectedAllAcrossPages) return;
        const removed = new Set(statuses);

        this.selectedMap.forEach((discSet, documentId) => {
            discSet.forEach((discId) => {
                if (removed.has(this.selectedStatusMap.get(discId) ?? '')) {
                    discSet.delete(discId);
                    this.selectedStatusMap.delete(discId);
                }
            });
            if (discSet.size === 0) this.selectedMap.delete(documentId);
        });

        if (this._gridApi && !this._gridApi.isDestroyed()) {
            this.selectionSyncInProgress = true;
            try {
                this._gridApi.forEachNode((node: IRowNode) => {
                    if (
                        node.data &&
                        node.isSelected() &&
                        removed.has(node.data.status)
                    ) {
                        node.setSelected(false);
                    }
                });
            } finally {
                this.selectionSyncInProgress = false;
            }
            this._gridApi.refreshHeader();
        }

        this.setSelectedRows();
    }

    private goToSavedPage() {
        if (this.firstFetch && this.savedPageIndex) {
            this._gridApi.paginationGoToPage(this.savedPageIndex - 1);
        }
        this.firstFetch = false;
    }

    private getQueryParams(request: IServerSideGetRowsRequest) {
        const pageSize = this._gridApi.paginationGetPageSize();
        const pageIndex = getPageIndex(request, pageSize);

        return {
            ...this.workflowAndRepo,
            ...{ states: this.states },
            ...{ mine: this.showMyItems },
            ...getFilterQuery(request.filterModel),
            ...getSortQuery(request.sortModel),
            pageIndex,
            pageSize,
        };
    }

    private onDiscrepancyIdClick(node: IRowNode) {
        this.store.dispatch(
            saveSummaryPanelData({
                selectedDocument: node.data.documentId,
                selectedDiscrepancy: node.data.id,
                selectedTab: DocumentDetailTab.DISCREPANCIES,
            }),
        );
    }

    private onDiscrepancyIdDoubleClick(node: IRowNode) {
        this.documentService
            .canEditDocument(node.data.documentId)
            .pipe(first())
            .subscribe({
                next: (canEdit) => {
                    if (canEdit) {
                        this.routerService.goDocumentDetails(
                            parseInt(node.data.documentId),
                            DocumentDetailTab.DISCREPANCIES,
                            parseInt(node.data.id),
                        );
                    }
                },
            });
    }

    private getColumnDefs(filterOptions: DiscrepancyFilterOptions): ColDef[] {
        return [
            {
                colId: 'select',
                headerName: '',
                checkboxSelection: true,
                headerCheckboxSelection: true,
                width: 40,
                minWidth: 40,
                maxWidth: 40,
                pinned: 'left' as const,
                filter: false,
                lockPosition: true,
                suppressMovable: true,
                resizable: false,
                suppressSizeToFit: true,
            },
            {
                field: DiscrepancyColumn.ID,
                headerName: 'Discrepancy ID',
                filter: 'agTextColumnFilter',
                minWidth: 145,
                valueFormatter: (params) => getDiscrepancyId(params.data).label,
                cellRenderer: ClickableCellComponent,
                cellRendererParams: {
                    dataQa: this.dataQa.discrepancyViewDiscrepancyIdCell,
                    tooltipGetter: (params: ICellRendererParams) =>
                        getDiscrepancyId(params.data).tooltip,
                    onClick: (node: IRowNode) =>
                        this.onDiscrepancyIdClick(node),
                    onDoubleClick: (node: IRowNode) =>
                        this.onDiscrepancyIdDoubleClick(node),
                },
                floatingFilterComponent: TextFilterComponent,
                floatingFilterComponentParams: {
                    dataQa: this.dataQa.discrepancyViewDiscrepancyIdFilter,
                    type: 'number',
                },
            },
            {
                field: DiscrepancyColumn.DOC_NAME,
                minWidth: 180,
                filter: 'agTextColumnFilter',
                cellRenderer: DocumentNameCellComponent,
                cellRendererParams: {
                    tooltipGetter: (params: ICellRendererParams) =>
                        params.data.documentName,
                },
                floatingFilterComponent: TextFilterComponent,
            },
            {
                field: DiscrepancyColumn.REASON,
                filter: 'agTextColumnFilter',
                minWidth: 130,
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.reason,
                    autocomplete: true,
                    noneOption: true,
                },
            },
            {
                field: DiscrepancyColumn.PRIORITY,
                maxWidth: 130,
                cellRenderer: PriorityCellComponent,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.priority,
                    priority: true,
                },
            },
            {
                field: DiscrepancyColumn.STATUS,
                minWidth: 180,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.status,
                },
            },
            {
                field: DiscrepancyColumn.AGE_IN_WF,
                headerName: 'Age In WF',
                maxWidth: 120,
                cellRenderer: GrayBoxCellComponent,
                valueFormatter: getAgeInWFValue,
                cellStyle: { textOverflow: 'clip' },
                filter: 'agTextColumnFilter',
                floatingFilterComponent: RangeFilterComponent,
                floatingFilterComponentParams: {
                    availableRange: new Range(0, filterOptions.maxAge),
                    title: 'Filter by Age Range',
                    inputLabel: 'days',
                },
            },
            {
                field: DiscrepancyColumn.TAKEN,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: DateFilterComponent,
            },
            {
                field: DiscrepancyColumn.ANALYST,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: TextFilterComponent,
            },
            {
                field: DiscrepancyColumn.STATION,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.station,
                    autocomplete: true,
                    noneOption: true,
                },
            },
            {
                field: DiscrepancyColumn.PRIMARY_FIXER,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.primaryFixer,
                    autocomplete: true,
                },
            },
            ...customColumnsToColDefs(
                this.customColumns,
                this.hiddenColumnOptions,
                filterOptions.customColumns,
            ),
        ];
    }
}
